import { Link } from '@tanstack/react-router';

import type {
  LibraryInstallResult,
  LibraryItemOutcome,
  LibraryOutcome,
} from '@/lib/api/generated/model';
import { kindLabel, outcomeLabel } from '../labels';
import { outcomeDestination } from '../outcomeDestination';

const ORDER: LibraryOutcome[] = [
  'added',
  'updated',
  'already_installed',
  'refused',
];

/**
 * What an install did, per item, grouped by what happened (spec 104 FR-010).
 *
 * Every item is named. A refusal carries the service's reason and nothing
 * else: the page never negotiates, and there is no "overwrite" here to offer.
 *
 * What was added or updated leads to where it landed (spec 105 FR-019): an
 * agent or a skill to its folder in the workspace's files, a workflow to
 * whichever page the product opens that workflow on. Nothing else is a link —
 * an item that was already installed needs no trip, and a refused one has
 * nothing at the other end.
 */
export function InstallOutcomes({
  result,
  workspaceId,
}: {
  result: LibraryInstallResult;
  workspaceId: string;
}) {
  const groups = new Map<LibraryOutcome, LibraryItemOutcome[]>();
  for (const outcome of result.outcomes ?? []) {
    groups.set(outcome.outcome, [
      ...(groups.get(outcome.outcome) ?? []),
      outcome,
    ]);
  }
  const substitutions = result.substitutions ?? [];
  const landed =
    (groups.get('added')?.length ?? 0) + (groups.get('updated')?.length ?? 0);
  const refused = groups.get('refused')?.length ?? 0;

  return (
    <div className="flex flex-col gap-3" aria-live="polite">
      {/* The sentence before the list. An install of a workflow with five
          dependencies reports six lines, and "what just happened" should not
          have to be counted off them. */}
      <p className="text-sm font-medium">
        {landed === 0
          ? 'Nothing was added.'
          : `${landed === 1 ? '1 item' : `${String(landed)} items`} added to this workspace.`}
        {refused > 0 &&
          ` ${refused === 1 ? '1 was' : `${String(refused)} were`} refused.`}
      </p>
      {ORDER.filter((key) => groups.has(key)).map((key) => (
        <section key={key} className="flex flex-col gap-1">
          <h3 className="text-sm font-medium">{outcomeLabel(key)}</h3>
          <ul aria-label={outcomeLabel(key)} className="flex flex-col gap-1">
            {groups.get(key)?.map((o) => {
              const to = outcomeDestination(o, workspaceId, result.workflow);
              return (
                <li key={`${o.kind}/${o.key}`} className="text-sm">
                  <span className="text-muted-foreground mr-1.5 text-xs">
                    {kindLabel(o.kind)}
                  </span>
                  {to ? (
                    <Link
                      {...to}
                      className="underline underline-offset-2 hover:no-underline"
                    >
                      {o.key}
                    </Link>
                  ) : (
                    o.key
                  )}
                  {o.reason && (
                    <span className="text-muted-foreground block text-xs">
                      {o.reason}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {substitutions.length > 0 && (
        <section className="flex flex-col gap-1">
          <h3 className="text-sm font-medium">Used instead</h3>
          <ul aria-label="Used instead" className="flex flex-col gap-1">
            {substitutions.map((s) => (
              <li key={`${s.kind}/${s.from_value}`} className="text-sm">
                {s.from_value} → {s.to_value}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
